import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import { encodePathSegment } from '../../lib/url';
import AuditTimeline, { AuditEvent } from '../../components/AuditTimeline';
import Badge from '../../components/Badge';

type LiveStatus = 'live' | 'reconnecting' | 'paused';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';
const PAGE_LIMIT = 50;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 30000;
const MAX_RECONNECT_ATTEMPTS = 6;

interface AuditLogEnvelope {
  items?: AuditEvent[];
  data?: AuditEvent[];
  nextCursor?: string | null;
  hasMore?: boolean;
}

interface TicketEnvelope {
  ticket: string;
  expiresInSec: number;
  agent: string;
}

function dedupeById(prev: AuditEvent[], incoming: AuditEvent[]): AuditEvent[] {
  if (incoming.length === 0) return prev;
  const seen = new Set(prev.map((e) => e.id));
  const merged = [...prev];
  for (const ev of incoming) {
    if (!seen.has(ev.id)) {
      merged.push(ev);
      seen.add(ev.id);
    }
  }
  // Newest first by occurredAt (fallback to id).
  merged.sort((a, b) => {
    const ta = Date.parse(a.occurredAt) || 0;
    const tb = Date.parse(b.occurredAt) || 0;
    if (tb !== ta) return tb - ta;
    return a.id < b.id ? 1 : -1;
  });
  return merged;
}

export default function AgentAuditPage() {
  const { did } = useParams<{ did: string }>();
  const { t } = useTranslation(['agents', 'audit', 'common']);

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [search, setSearch] = useState('');

  // Live mode + reconnect
  const [liveOn, setLiveOn] = useState(true);
  const [liveStatus, setLiveStatus] = useState<LiveStatus>('paused');
  const esRef = useRef<EventSource | null>(null);
  const lastIdRef = useRef<string | undefined>(undefined);
  const attemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedRef = useRef(false);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      if (!did) return;
      const search = new URLSearchParams();
      search.set('limit', String(PAGE_LIMIT));
      if (cursor) search.set('cursor', cursor);
      const path = `/agents/${encodePathSegment(did)}/audit-log?${search.toString()}`;
      const res = await apiFetch<AuditLogEnvelope>(path);
      const items = Array.isArray(res.items)
        ? res.items
        : Array.isArray(res.data)
          ? res.data
          : [];
      return {
        items,
        nextCursor: res.nextCursor ?? null,
        hasMore: Boolean(res.hasMore),
      };
    },
    [did],
  );

  const loadInitial = useCallback(async () => {
    if (!did) return;
    setLoading(true);
    setError(null);
    try {
      const page = await fetchPage(null);
      if (!page) return;
      setEvents(dedupeById([], page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      // Track last seen id (newest) for SSE resume.
      if (page.items.length > 0) {
        const sorted = [...page.items].sort((a, b) => {
          const ta = Date.parse(a.occurredAt) || 0;
          const tb = Date.parse(b.occurredAt) || 0;
          return tb - ta;
        });
        lastIdRef.current = sorted[0]?.id;
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('audit.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [did, fetchPage, t]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const loadOlder = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchPage(nextCursor);
      if (!page) return;
      setEvents((prev) => dedupeById(prev, page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('audit.loadFailed'));
    } finally {
      setLoadingMore(false);
    }
  };

  // ---- SSE live stream ----
  const teardownStream = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (esRef.current) {
      try {
        esRef.current.close();
      } catch {
        /* ignore */
      }
      esRef.current = null;
    }
  }, []);

  const ingestFrame = useCallback((payload: string, frameId?: string) => {
    let parsed: AuditEvent | AuditEvent[] | null = null;
    try {
      parsed = JSON.parse(payload) as AuditEvent | AuditEvent[];
    } catch {
      return;
    }
    const incoming = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
    if (incoming.length === 0) return;
    setEvents((prev) => dedupeById(prev, incoming));
    const newestId = incoming[incoming.length - 1]?.id;
    if (newestId) lastIdRef.current = newestId;
    if (frameId) lastIdRef.current = frameId;
  }, []);

  const connect = useCallback(async () => {
    if (!did) return;
    if (closedRef.current) return;
    setLiveStatus(attemptsRef.current === 0 ? 'reconnecting' : 'reconnecting');

    let ticket: string;
    try {
      const ticketRes = await apiFetch<TicketEnvelope>(
        `/agents/${encodePathSegment(did)}/audit-stream/ticket`,
        { method: 'POST', body: '{}' },
      );
      ticket = ticketRes.ticket;
    } catch {
      attemptsRef.current += 1;
      if (attemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
        setLiveStatus('paused');
        setLiveOn(false);
        return;
      }
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (attemptsRef.current - 1));
      reconnectTimerRef.current = setTimeout(() => {
        void connect();
      }, delay);
      return;
    }

    const params = new URLSearchParams();
    params.set('ticket', ticket);
    if (lastIdRef.current) params.set('lastEventId', lastIdRef.current);
    const url = `${API_BASE}/agents/${encodePathSegment(did)}/audit-stream?${params.toString()}`;

    let es: EventSource;
    try {
      es = new EventSource(url);
    } catch {
      setLiveStatus('paused');
      setLiveOn(false);
      return;
    }
    esRef.current = es;

    es.onopen = () => {
      attemptsRef.current = 0;
      setLiveStatus('live');
    };

    const onMessage = (ev: MessageEvent) => {
      ingestFrame(ev.data, ev.lastEventId || undefined);
    };
    es.addEventListener('audit', onMessage as EventListener);
    es.onmessage = onMessage;

    es.onerror = () => {
      if (closedRef.current) return;
      teardownStream();
      attemptsRef.current += 1;
      if (attemptsRef.current > MAX_RECONNECT_ATTEMPTS) {
        setLiveStatus('paused');
        setLiveOn(false);
        return;
      }
      setLiveStatus('reconnecting');
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (attemptsRef.current - 1));
      reconnectTimerRef.current = setTimeout(() => {
        void connect();
      }, delay);
    };
  }, [did, ingestFrame, teardownStream]);

  useEffect(() => {
    if (!liveOn || !did) {
      closedRef.current = true;
      teardownStream();
      setLiveStatus('paused');
      return;
    }
    closedRef.current = false;
    attemptsRef.current = 0;
    void connect();
    return () => {
      closedRef.current = true;
      teardownStream();
    };
  }, [liveOn, did, connect, teardownStream]);

  // Derive available event types from received events.
  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    for (const ev of events) set.add(ev.eventType as string);
    return Array.from(set).sort();
  }, [events]);

  const filteredEvents = useMemo(() => {
    const sinceMs = since ? Date.parse(since) : null;
    const untilMs = until ? Date.parse(until) : null;
    const q = search.trim().toLowerCase();
    return events.filter((ev) => {
      if (selectedTypes.size > 0 && !selectedTypes.has(ev.eventType as string)) return false;
      const occurredMs = Date.parse(ev.occurredAt);
      if (sinceMs !== null && !Number.isNaN(occurredMs) && occurredMs < sinceMs) return false;
      if (untilMs !== null && !Number.isNaN(occurredMs) && occurredMs >= untilMs) return false;
      if (q) {
        const actor = (ev.actorDid ?? '').toLowerCase();
        if (!actor.includes(q)) return false;
      }
      return true;
    });
  }, [events, selectedTypes, since, until, search]);

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const clearFilters = () => {
    setSelectedTypes(new Set());
    setSince('');
    setUntil('');
    setSearch('');
  };

  const liveBadge = useMemo(() => {
    switch (liveStatus) {
      case 'live':
        return { variant: 'success' as const, label: t('audit.live.live'), tip: t('audit.live.tooltipLive'), pulse: true };
      case 'reconnecting':
        return {
          variant: 'warning' as const,
          label: t('audit.live.reconnecting'),
          tip: t('audit.live.tooltipReconnecting'),
          pulse: true,
        };
      case 'paused':
      default:
        return { variant: 'neutral' as const, label: t('audit.live.paused'), tip: t('audit.live.tooltipPaused'), pulse: false };
    }
  }, [liveStatus, t]);

  if (!did) {
    return (
      <div className="text-center py-20 text-red-500">
        {t('common:errors.unknown')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-xs text-text-faint">
        <Link to="/ai-agents" className="hover:text-primary">
          {t('detail.breadcrumbBase')}
        </Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <Link to={`/ai-agents/${did}`} className="hover:text-primary">
          {did}
        </Link>
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
        <span className="font-semibold text-text-main">{t('audit.title')}</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-text-main">{t('audit.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('audit.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <span title={liveBadge.tip}>
            <Badge variant={liveBadge.variant} pulse={liveBadge.pulse}>
              {liveBadge.label}
            </Badge>
          </span>
          <label className="flex cursor-pointer items-center gap-2 text-xs font-bold text-text-muted">
            <input
              type="checkbox"
              checked={liveOn}
              onChange={(e) => setLiveOn(e.target.checked)}
              className="rounded"
            />
            {t('audit.live.label')}
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">
            {t('audit.filters.title')}
          </span>
          <button
            onClick={clearFilters}
            className="text-[10px] font-bold text-primary hover:underline"
          >
            {t('audit.filters.clear')}
          </button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <label className="mb-1.5 block text-xs font-bold text-text-muted">
              {t('audit.filters.eventType')}
            </label>
            {availableTypes.length === 0 ? (
              <p className="text-[11px] text-text-faint">{t('audit.filters.anyEventType')}</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {availableTypes.map((type) => {
                  const active = selectedTypes.has(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggleType(type)}
                      className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors ${
                        active
                          ? 'border-primary bg-primary text-white'
                          : 'border-slate-200 bg-white text-text-muted hover:bg-slate-50'
                      }`}
                    >
                      {t(`audit:eventTypes.${type}`, type)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold text-text-muted">
              {t('audit.filters.since')}
            </label>
            <input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 text-xs text-text-main focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold text-text-muted">
              {t('audit.filters.until')}
            </label>
            <input
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 text-xs text-text-main focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
          <div className="lg:col-span-4">
            <label className="mb-1.5 block text-xs font-bold text-text-muted">
              {t('audit.filters.search')}
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('audit.filters.searchPlaceholder')}
              className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 font-mono text-xs text-text-main placeholder:text-text-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs font-semibold text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-text-faint">
        <span>{t('audit.totalCount', { count: filteredEvents.length })}</span>
        {events[0]?.occurredAt && (
          <span>
            {t('audit.lastEvent', { time: new Date(events[0].occurredAt).toLocaleString() })}
          </span>
        )}
      </div>

      {loading ? (
        <div className="rounded-xl border border-border-subtle bg-white p-10 text-center text-sm text-text-faint">
          {t('audit.loading')}
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="rounded-xl border border-border-subtle bg-white p-10 text-center text-sm text-text-faint">
          {t('audit.noResults')}
        </div>
      ) : (
        <AuditTimeline events={filteredEvents} />
      )}

      {hasMore && (
        <div className="flex justify-center">
          <button
            onClick={loadOlder}
            disabled={loadingMore}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-text-muted hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="material-symbols-outlined mr-1 align-middle" style={{ fontSize: 14 }}>
              expand_more
            </span>
            {loadingMore ? t('common:loading') : t('audit.loadOlder')}
          </button>
        </div>
      )}
    </div>
  );
}
